/**
 * AppActionRenderer — handles `app_action` content items.
 *
 * Two delivery modes selected by the item's `auto` flag:
 *
 *   auto=false (default) → renders as a primary/secondary CTA. User taps
 *                          to fire. Use for "Continue to checkout" type
 *                          buttons after a card summary.
 *   auto=true            → fires once on mount via a useRef guard. Use
 *                          AFTER the user has confirmed intent. Always
 *                          pair with a `rationale` so the user sees why
 *                          we just navigated/changed state.
 *
 * History re-renders never auto-fire: when the user scrolls back through
 * an old message, we don't want to re-navigate them. The button form
 * stays tappable in history; the auto form renders the rationale (so the
 * past nudge stays visible) without re-firing.
 */
import React, { useEffect, useRef } from 'react';
import type { AppActionContentItem } from '@wg/types';
import type { Renderer } from '../registry';
import { executeAppAction } from '../actions';
import logger from '../../utils/logger';

const AppActionRenderer: Renderer = ({ item, ctx }) => {
  const a = item as AppActionContentItem;
  const fired = useRef(false);

  // Auto-fire path. Guarded against re-mount loops AND history re-renders.
  useEffect(() => {
    if (!a.auto) return;
    if (ctx.isHistory) return;
    if (fired.current) return;
    if (!ctx.actionDeps) {
      logger.warn('[AppActionRenderer] auto-fire skipped — no actionDeps');
      return;
    }
    fired.current = true;
    executeAppAction(a.verb, a.args, ctx.actionDeps);
  }, [a, ctx]);

  // Auto items render only the rationale (or nothing) — they're a side
  // effect, not a UI element the user tapped to fire.
  if (a.auto) {
    if (!a.rationale) return null;
    return (
      <div className="wg-chat__app-action wg-chat__app-action--auto">
        <span className="wg-chat__app-action-icon" aria-hidden>
          ↗
        </span>
        <span className="wg-chat__app-action-rationale">{a.rationale}</span>
      </div>
    );
  }

  // Tap form — primary/secondary button styled like CardRenderer's actions.
  return (
    <div className="wg-chat__app-action">
      <button
        type="button"
        className={`wg-chat__app-action-btn wg-chat__app-action-btn--${a.weight ?? 'primary'}`}
        onClick={() => {
          if (!ctx.actionDeps) {
            logger.warn('[AppActionRenderer] tap fired without actionDeps');
            return;
          }
          executeAppAction(a.verb, a.args, ctx.actionDeps);
        }}
        disabled={ctx.isHistory}
      >
        {a.label || defaultLabel(a.verb)}
      </button>
      {a.rationale && (
        <span className="wg-chat__app-action-rationale">{a.rationale}</span>
      )}
    </div>
  );
};

function defaultLabel(verb: string): string {
  switch (verb) {
    case 'navigate':
      return 'Open';
    case 'prefill_checkout':
      return 'Continue to checkout';
    case 'submit_message':
      return 'Send';
    case 'request_close':
      return 'Close';
    case 'open_external':
      return 'Open link';
    default:
      return verb;
  }
}

export default AppActionRenderer;
